import React, { useState, useEffect } from 'react';

// --- CSS Styles ---
// Using a template string to inject CSS ensures single-file portability
const cssStyles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;900&display=swap');

.random-preview {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, #f9f9f9 0%, #ffffff 100%);
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 9999;
  opacity: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.random-preview.fade-in {
  animation: fadeIn 300ms ease-out forwards;
}

.random-preview.fade-out {
  animation: fadeOut 300ms ease-in forwards;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* LEVITATE ANIMATION (Replaces the "Breathe/Zoom" effect) */
@keyframes levitate {
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-8px); /* Gently floats up */
  }
}

.preview-content {
  padding: 0 20px;
  max-width: 1200px;
  width: 100%;
  will-change: transform;
}

/* Apply the floating animation */
.preview-content.floating {
  animation: levitate 4s ease-in-out infinite; 
}

.preview-text {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", "Arial", sans-serif;
  font-size: clamp(1.5rem, 4vw, 3rem);
  color: #000000;
  text-align: center;
  margin: 0;
  line-height: 1.6;
  word-wrap: break-word;
  font-weight: 400;
  text-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.preview-text.bold-text {
  font-weight: 900;
  letter-spacing: -0.02em;
}

.cursor {
  font-weight: 400;
  transition: opacity 0.1s ease-in-out;
  margin-left: 2px;
  color: #000;
}

.cursor.visible { opacity: 1; }
.cursor.hidden { opacity: 0; }

@media (max-width: 768px) {
  .preview-text {
    font-size: clamp(1.2rem, 5vw, 2rem);
  }
}
`;

// --- RandomPreview Component ---
const RandomPreview = ({ onComplete }) => {
  const config = {
    phrases: [
      "good timing.",
      "let's build something useful.",
      "clean systems, real outcomes.",
      "ship faster with less chaos.",
      "automation beats repetition.",
      "inbox open, ideas welcome.",
      "we can turn this into a product.",
      "steady execution, minimal noise.",
      "ready when you are.",
      "let's make this practical."
    ],
    display_ms: 3000,
    fade_ms: 300,
    typing_duration_ms: 2500 // Smooth typing duration
  };

  const [randomPhrase] = useState(() => {
    return config.phrases[Math.floor(Math.random() * config.phrases.length)];
  });
  const [displayText, setDisplayText] = useState('');
  const [fadeOut, setFadeOut] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const fullText = randomPhrase;
    let animationFrameId;
    let startTime;
    let fadeTimer;
    let completeTimer;

    const animateTyping = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / config.typing_duration_ms, 1);

      const charIndex = Math.floor(progress * fullText.length);
      setDisplayText(fullText.substring(0, charIndex));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateTyping);
      } else {
        setDisplayText(fullText);

        fadeTimer = setTimeout(() => {
          setFadeOut(true);
        }, config.display_ms);

        completeTimer = setTimeout(() => {
          onComplete();
        }, config.display_ms + config.fade_ms);
      }
    };

    animationFrameId = requestAnimationFrame(animateTyping);

    const cursorBlink = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => {
      cancelAnimationFrame(animationFrameId);
      clearInterval(cursorBlink);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [randomPhrase, config.display_ms, config.fade_ms, config.typing_duration_ms, onComplete]);

  return (
    <>
      <style>{cssStyles}</style>
      <div className={`random-preview ${fadeOut ? 'fade-out' : 'fade-in'}`}>
        <div className="preview-content floating">
          <h1 className="preview-text bold-text">
            {displayText}
            <span className={`cursor ${showCursor ? 'visible' : 'hidden'}`}>|</span>
          </h1>
        </div>
      </div>
    </>
  );
};

export default RandomPreview;
